/** @format */

/**
 * Span 原始日志抽屉：以 JSON 只读展示当前 Span，由父组件控制 visible / onClose。
 */

import React, { useCallback, useEffect, useRef } from 'react'
import { Controlled as CodeMirror } from 'react-codemirror2'
import type { Editor } from 'codemirror'
import styled from 'styled-components'
import { _$ } from '@king-design/react'
import { CopyButton } from 'src/components'

import 'codemirror/lib/codemirror.css'
import 'codemirror/theme/material.css'
import 'codemirror/mode/javascript/javascript'

const EDITOR_HEADER_HEIGHT = 36
const EDITOR_BODY_HEIGHT = 'calc(100vh - 160px)'
const EDITOR_HEIGHT = `calc(100vh - ${160 + EDITOR_HEADER_HEIGHT}px)`

export interface JsonCodeProps {
  className?: string
  title?: string
  jsonstring: string
  height?: string | number
}

const JsonCode: React.FC<JsonCodeProps> = ({
  className,
  title,
  jsonstring,
  height = EDITOR_BODY_HEIGHT
}) => {
  const editorRef = useRef<Editor | null>(null)

  const refreshEditor = useCallback(() => {
    const editor = editorRef.current
    if (!editor) {
      return
    }
    editor.setSize('100%', height)
    editor.refresh()
  }, [])

  /** 抽屉展开动画结束后再 refresh，避免初始宽高为 0 导致空白 */
  useEffect(() => {
    if (!jsonstring) {
      return undefined
    }
    const timer = window.setTimeout(() => {
      refreshEditor()
    }, 150)
    return () => window.clearTimeout(timer)
  }, [jsonstring, refreshEditor])

  const handleBeforeChange = useCallback(() => {
    // 原始日志只读展示，不接收编辑内容。
  }, [])

  const handleEditorDidMount = useCallback(
    (editor: Editor) => {
      editorRef.current = editor
      window.setTimeout(() => {
        refreshEditor()
      }, 150)
    },
    [refreshEditor]
  )

  return (
    <div className={className}>
      {jsonstring ? (
        <div className="json-editor">
          <div className="json-editor-header">
            <span className="json-editor-title" title={title}>
              {title}
            </span>
            <CopyButton text={jsonstring} color="#fff" />
          </div>
          <CodeMirror
            value={jsonstring}
            onBeforeChange={handleBeforeChange}
            editorDidMount={handleEditorDidMount}
            options={{
              mode: 'application/json',
              theme: 'material',
              lineNumbers: true,
              lineWrapping: true,
              readOnly: true,
              foldGutter: true,
              gutters: ['CodeMirror-linenumbers', 'CodeMirror-foldgutter'],
              matchBrackets: true,
              indentUnit: 2,
              tabSize: 2,
              indentWithTabs: false
            }}
          />
        </div>
      ) : (
        <p className="json-editor-empty">{_$('暂无数据')}</p>
      )}
    </div>
  )
}

export default styled(JsonCode)`
  min-height: 320px;
  height: ${props => props.height};

  .json-editor {
    display: flex;
    flex-direction: column;
    height: 100%;
    border-radius: 4px;
    overflow: hidden;
  }

  .json-editor-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-shrink: 0;
    height: ${EDITOR_HEADER_HEIGHT}px;
    padding: 0 12px;
    background: #232324;
    .k-icon-clone {
      color: #fff;
    }
  }

  .json-editor-title {
    flex: 1;
    min-width: 0;
    margin-right: 12px;
    font-size: 14px;
    font-weight: 500;
    color: #fff;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .CodeMirror {
    flex: 1;
    height: ${EDITOR_HEIGHT};
    border: none;
    border-radius: 0;
    font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
    font-size: 14px;
    line-height: 1.5;
    background-color: #17171a;
  }

  .cm-s-material.CodeMirror,
  .cm-s-material .CodeMirror-gutters {
    background-color: #17171a;
  }

  .CodeMirror-linenumber {
    color: #86909c;
    font-size: 12px;
  }

  .json-editor-empty {
    margin: 48px 0;
    text-align: center;
    color: rgba(0, 0, 0, 0.45);
    font-size: 13px;
  }
`
